<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    show: Boolean,
    isEditing: Boolean,
    loading: Boolean,
    initialData: {
        type: Object,
        default: () => ({ id_suppliers: null, name: "", phone: "", email: "", address: "", image_url: null })
    }
});

const emit = defineEmits(['close', 'save']);

const form = ref({ ...props.initialData });
const file = ref(null);
const previewUrl = ref(null);

watch(() => props.initialData, (newVal) => {
    form.value = { ...newVal };
    previewUrl.value = newVal.image_url || null;
    file.value = null;
}, { deep: true });

function onClose() {
    emit('close');
}

function onFileSelect(e) {
    const selected = e.target.files[0];
    if (selected) {
        file.value = selected;
        previewUrl.value = URL.createObjectURL(selected);
    }
}

function removeImage() {
    file.value = null;
    previewUrl.value = null;
    form.value.image_url = null; // Mark for deletion
}

function onSave() {
    emit('save', form.value, file.value);
}
</script>

<template>
    <div v-if="show" class="modal-backdrop" @click.self="onClose">
        <div class="modal-content">
            <header class="modal-header">
                <h2>{{ isEditing ? 'Editar Proveedor' : 'Nuevo Proveedor' }}</h2>
                <button class="close-modal-btn" @click="onClose">
                    <span class="material-symbols-rounded">close</span>
                </button>
            </header>

            <form @submit.prevent="onSave" class="modal-form">
                <!-- Image Upload Section -->
                <div class="form-group center-group">
                    <label>Logo / Imagen</label>
                    <div class="image-upload-wrapper">
                        <div v-if="previewUrl" class="image-preview">
                            <img :src="previewUrl" alt="Preview" />
                        </div>
                        <button v-if="previewUrl" type="button" class="btn-remove-img" @click="removeImage"
                            title="Quitar imagen">
                            <span class="material-symbols-rounded">close</span>
                        </button>

                        <div v-if="!previewUrl" class="image-placeholder">
                            <span class="material-symbols-rounded icon">add_photo_alternate</span>
                        </div>
                        <input type="file" accept="image/*" @change="onFileSelect" class="file-input" />
                    </div>
                </div>

                <div class="form-group">
                    <label>Nombre *</label>
                    <input v-model="form.name" type="text" required placeholder="Ej. Distribuidora Central" />
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>Teléfono</label>
                        <input v-model="form.phone" type="text" placeholder="55 1234 5678" />
                    </div>
                    <div class="form-group">
                        <label>Email</label>
                        <input v-model="form.email" type="email" placeholder="contacto@ejemplo.com" />
                    </div>
                </div>

                <div class="form-group">
                    <label>Dirección</label>
                    <textarea v-model="form.address" rows="3" placeholder="Calle, Número, Colonia, Ciudad"></textarea>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn-secondary" @click="onClose">Cancelar</button>
                    <button type="submit" class="btn-primary" :disabled="loading">
                        <span v-if="loading" class="spinner-sm"></span>
                        <span v-else>{{ isEditing ? 'Guardar Cambios' : 'Crear Proveedor' }}</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped>
.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    z-index: 10000;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    animation: fadeIn 0.2s ease-out;
}

.modal-content {
    background: var(--panel-bg);
    width: 100%;
    max-width: 500px;
    border-radius: 1.5rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    border: 1px solid var(--border);
}

.modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text);
}

.close-modal-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--muted);
    padding: 0.25rem;
    border-radius: 50%;
    display: grid;
    place-items: center;
}

.close-modal-btn:hover {
    background: var(--surface-2);
    color: var(--text);
}

.modal-form {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.center-group {
    align-items: center;
}

.image-upload-wrapper {
    position: relative;
    width: 100px;
    height: 100px;
}

.file-input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 10;
}

.image-preview,
.image-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: var(--surface-2);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed var(--border);
    position: relative;
    box-sizing: border-box;
}

.image-preview {
    border-style: solid;
    border-color: var(--accent);
}

.image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-placeholder .icon {
    font-size: 2rem;
    color: var(--muted);
}

.btn-remove-img {
    position: absolute;
    top: 0;
    right: 0;
    background: var(--surface);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 50%;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 20;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transition: all 0.2s;
}

.btn-remove-img:hover {
    background: var(--surface-2);
    color: #ef4444;
    transform: scale(1.1);
}

.btn-remove-img span {
    font-size: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-group label {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text);
}

.form-group input:not([type="file"]),
.form-group textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    background: var(--bg);
    color: var(--text);
    font-family: inherit;
    font-size: 0.95rem;
    transition: all 0.2s;
}

.form-group input:not([type="file"]):focus,
.form-group textarea:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 92, 255, 0.1);
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}

.btn-primary {
    background: var(--accent);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
    filter: brightness(1.1);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(124, 92, 255, 0.25);
}

.btn-secondary {
    background: transparent;
    color: var(--text);
    border: 1px solid var(--border);
    padding: 0.75rem 1.5rem;
    border-radius: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: var(--surface-2);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner-sm {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

/* Responsive */
@media (max-width: 768px) {
    .form-row {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }
}
</style>
